<script lang="ts">
	import {
		Card,
		SlotSelect,
		TopBottom,
		AdaptiveTextInput,
		OutlineButton,
		PlusIcon,
		XIcon,
	} from "@hitit/new_ui";
	import StoreNameValue from "./StoreNameValue.svelte";
	import ComponentCover from "../common/ComponentCover.svelte";
	import ComponentLoading from "../common/ComponentLoading.svelte";
	import { format, parse, isValid } from "date-fns";
	//Utils;
	import type { StoreData } from "@hitit/api";
	import {
		getNationalPhonenumber,
		addSpacer,
	} from "../../../functions/utils/phone";
	import { t, api } from "@hitit/hub/src/stores";
	import { parsePhoneNumber } from "libphonenumber-js";
	//Image
	import EditIcon from "@hitit/hub/src/assets/Icons/Edit.png";
	import SaveIcon from "@hitit/hub/src/assets/Icons/Save.png";

	//
	export let store: StoreData;
	export let editmode = false;
	export let editable = true;
	export let coverStatus = false;

	type Recipient = {
		phonenumber: string | null;
		phonenumberCode: string | null;
		error: string;
	};

	const lastInputBoxIndex = 4;
	const hours = Array.from(Array(24).keys()).map((_, index) => {
		const hour = index.toString().length === 1 ? "0" + index : index;
		return {
			label: `${hour}:00`,
			value: `${hour}:00`,
			display: `${hour}:00`,
		};
	});

	let loading = false;
	let inputs: Recipient[] = [];

	async function actionHandler() {
		if (!editmode) {
			editmode = true;
			return;
		}

		try {
			loading = true;
			const filteredAdditionalInfo = store.storeAdditionalInfoDTOList.filter(
				(v) =>
					v.key !== "DAILY_REPORT_START_TIME" &&
					v.key !== "DAILY_REPORT_RECIPIENT"
			);

			const parsedTime = parse(time, "HH:mm", new Date());
			const formattedTime = format(parsedTime, "HH:mm:ss");
			const phoneError = inputs.reduce((acc, recipient, index) => {
				if (recipient.phonenumber === null) {
					return acc;
				}

				let error = false;
				try {
					const parsedPhone = parsePhoneNumber(
						`+${recipient.phonenumberCode} ${recipient.phonenumber}`
					);
					if (!parsedPhone.isValid()) error = true;
				} catch (e) {
					error = true;
				}
				inputs[index].error = error
					? $t("hub.revenue_message.phone_error")
					: "";

				return acc || Boolean(inputs[index].error);
			}, false);

			if (phoneError) {
				return;
			}

			const parsedRecipients = inputs
				.filter((v) => v.phonenumber !== null && v.phonenumberCode !== null)
				.map((recipient) => {
					const parsed = parsePhoneNumber(
						`+${recipient.phonenumberCode} ${recipient.phonenumber}`
					);
					if (!parsed) throw new Error("Phonenumber was not parsed!");
					return {
						phonenumber: parsed.nationalNumber,
						phonenumberCode: parsed.countryCallingCode,
					};
				});

			store.storeAdditionalInfoDTOList = [
				...filteredAdditionalInfo,
				{ key: "DAILY_REPORT_START_TIME", value: formattedTime },
			];

			if (parsedRecipients.length > 0) {
				store.storeAdditionalInfoDTOList.push({
					key: "DAILY_REPORT_RECIPIENT",
					value: parsedRecipients
						.map((v) => `${v.phonenumberCode}_${v.phonenumber}`)
						.join("|"),
				});
			}
			await $api.updateStore(store);
			store = store;
			editmode = false;
		} catch (e) {
			console.log(e);
		} finally {
			loading = false;
		}
	}

	function addHandler() {
		const arr = [];

		arr.push(...inputs, {
			phonenumberCode: "82",
			phonenumber: "",
			error: "",
		});

		inputs = arr;
	}

	function removeHandler(index: number) {
		inputs = [...inputs.filter((v, i) => i !== index)];
	}

	function selectChange(e: Event) {
		const target = e.target as HTMLSelectElement;
		time = target.value;
	}

	function parseKoreanPhoneFormat(phone: string | null, code: string | null) {
		phone = phone ? phone : "";
		code = code ? code : "";
		return addSpacer(getNationalPhonenumber(phone, code));
	}

	function onInput(e: Event, index: number) {
		const target = e.currentTarget as HTMLInputElement;
		const { value } = target;
		const originalCaret = target.selectionEnd;
		const originalGap = value.length - (originalCaret || 0);
		const replace = value
			.replace(/-/g, "") //"-"제거
			.replace(/[^0-9]/g, ""); //숫자를제거한 나머지 모두 제거
		const newValue = addSpacer(replace); //다시 addSpacer
		target.value = newValue;
		target.focus();

		//캐럿을 따로 설정해주지 않으면, target.value = newValue로 인해서 캐럿이 항상 마지막으로 이동한다.
		const newPosition = newValue.length - originalGap;
		target.setSelectionRange(newPosition, newPosition);

		inputs[index].phonenumber = replace;
	}

	function inputGenerate() {
		inputs = recipients.map((v) => v);
	}

	$: timeObject = store.storeAdditionalInfoDTOList.find(
		(v) => v.key === "DAILY_REPORT_START_TIME"
	) || { key: "DAILY_REPORT_START_TIME", value: "" };
	$: recipientObject = store.storeAdditionalInfoDTOList.find(
		(v) => v.key === "DAILY_REPORT_RECIPIENT"
	) || { key: "DAILY_REPORT_RECIPIENT", value: "" };

	$: parsedTime = parse(
		timeObject?.value || "00:00:00",
		"HH:mm:ss",
		new Date()
	);
	$: time = isValid(parsedTime) ? format(parsedTime, "HH:") + "00" : "00:00";
	$: recipients = recipientObject.value
		.split("|")
		.filter((v) => v !== "")
		.map((v) => ({
			phonenumberCode: v.split("_")[0],
			phonenumber: v.split("_")[1],
			error: "",
		})) as Recipient[];
	$: editmode, inputGenerate();
</script>

<Card noPadding>
	<div class="container" class:editmode={editmode} t-id="salestransfer">
		{#if coverStatus}
			<ComponentCover />
		{/if}
		{#if loading}
			<ComponentLoading coverMode />
		{/if}
		{#if editable}
			<div class="action" on:click={actionHandler}>
				<span>{editmode ? $t("global.done") : $t("global.edit")}</span>
				<img
					src={editmode ? SaveIcon : EditIcon}
					alt={editmode ? "save" : "edit"}
				/>
			</div>
		{/if}
		<StoreNameValue
			description={$t("hub.store.storeinfo.kakao_tooltip")}
			name={$t("hub.revenue_message.time_when_sales_settlement_begins")}
			editmode={editmode}
			value={time}
		>
			<div slot="input" class="select">
				<SlotSelect
					items={hours}
					bind:value={time}
					on:change={selectChange}
					let:displayValue
					backgroundColor={"#f0f0f4"}
				>
					<div class="inner">
						{displayValue}
						<div class="bottom">
							<TopBottom />
						</div>
					</div>
				</SlotSelect>
			</div>
		</StoreNameValue>
		{#if !editmode}
			<div class="seperate-bar" />
		{/if}
		<StoreNameValue
			name={$t("hub.revenue_message.recipient_info")}
			editmode={editmode}
		>
			<div slot="input" class="input-wrapper">
				{#each inputs as recipient, index}
					<div class="input-box">
						<div class="input">
							<AdaptiveTextInput
								on:input={(e) => onInput(e, index)}
								placeholder={$t("hub.store.storeinfo.enter_phonenumber")}
								value={parseKoreanPhoneFormat(
									recipient.phonenumber,
									recipient.phonenumberCode
								)}
								clearButtonForceDisplay
								backgroundColor={"#f0f0f4"}
							>
								<div
									slot="clear"
									class="clear"
									on:click={() => removeHandler(index)}
								>
									<XIcon />
								</div>
							</AdaptiveTextInput>
						</div>
						{#if recipient.error}
							<div class="error">{recipient.error}</div>
						{/if}
					</div>
				{/each}
				{#if inputs.length < lastInputBoxIndex}
					<div class="add-button">
						<OutlineButton
							noneBordercolor
							fitmode
							center
							nonePadding
							on:click={addHandler}
						>
							<div class="plus">
								<PlusIcon />
							</div>
						</OutlineButton>
					</div>
				{/if}
			</div>
			<div slot="value" class="value">
				{#each recipients as recipient}
					<p>
						{parseKoreanPhoneFormat(
							recipient.phonenumber || "",
							recipient.phonenumberCode || ""
						)}
					</p>
				{/each}
			</div>
		</StoreNameValue>
	</div>
</Card>

<style lang="scss">
	@import "../../../variables.scss";

	.container {
		display: flex;
		width: 100%;
		flex-direction: column;
		gap: 10px;
		padding: 20px 10px;
		position: relative;

		&.editmode {
			gap: 18px;
		}

		.seperate-bar {
			width: 100%;
			height: 1px;
			background-color: #e6e7ec;
		}

		.value {
			display: flex;
			flex-direction: column;
			gap: 10px;
			color: #46474b;
			font-weight: 500;
		}
		.select {
			width: 100%;
			height: 31px;
		}
		.inner {
			width: 100%;
			height: 100%;
			align-items: center;
			display: flex;
			padding: 0px 10px;
			position: relative;
			color: #46474b;

			.bottom {
				width: 20px;
				height: 100%;
				position: absolute;
				right: 10px;
				top: 50%;
				transform: translate(0%, -50%);
			}
		}

		.input-wrapper {
			display: flex;
			width: 100%;
			flex-direction: column;
			gap: 10px;

			.input {
				height: 31px;
				color: #46474b;
			}

			.add-button {
				box-sizing: border-box;
				width: 100%;
				height: 31px;
				border: 1px solid #bcbfc3;
				border-radius: 5px;

				.plus {
					width: 20.5px;
					height: 20.5px;
				}
			}

			.error {
				margin-top: 5px;
				font-size: 0.85rem;
				color: #f66d6c;
			}

			.clear {
				width: 15px;
				height: 15px;
				&:hover {
					cursor: pointer;
				}
			}
		}

		.action {
			display: flex;
			gap: 10px;
			align-items: center;
			position: absolute;
			right: 10px;
			top: 10px;
			color: #6d6f76;
			font-weight: 500;

			&:hover {
				cursor: pointer;
			}
			img {
				width: 24px;
				height: 24px;
				object-fit: contain;
			}
		}
	}

	@include DesktopSize {
		.container {
			padding: 30px;
			gap: 20px;
			.action {
				top: 20px;
				right: 20px;
				font-size: 1.25rem;
			}
			&.editmode {
				gap: 10px;
			}
			.inner {
				font-weight: 500;
				font-size: 1.5rem;
			}
			.value {
				font-size: 1.25rem;
				gap: 10px 0px;
				p {
					width: 50%;
				}
			}

			.input-wrapper {
				gap: 10px 20px;

				.input {
					height: 40px;

					:global(input) {
						font-size: 1.5rem;
					}
				}
				.add-button {
					height: 40px;
				}
			}

			.select {
				width: 100%;
				height: 40px;
			}
			.input-wrapper {
				.input-box,
				.add-button {
					width: 100%;
				}
			}
		}
	}
</style>
